import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import toast from 'react-hot-toast';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const UserIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const PAGE_SIZE = 10;

const selectStyle = {
  background: 'var(--card-solid)', border: '1.5px solid var(--border)',
  borderRadius: 9, color: 'var(--text)', padding: '8px 12px',
  fontSize: 13, fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 600,
  outline: 'none', cursor: 'pointer',
};

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 20,
};

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [sortDir, setSortDir]   = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [page, setPage]         = useState(1);
  const [selected, setSelected] = useState(null);   // student for detail modal
  const [delTarget, setDelTarget] = useState(null); // student for confirm delete
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/admin/users')
      .then(r => setStudents(r.data || []))
      .catch(() => toast.error('Failed to load students'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    let list = [...students];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.phone?.toLowerCase().includes(q)
      );
    }
    if (dateFrom) list = list.filter(s => new Date(s.createdAt) >= new Date(dateFrom));
    if (dateTo)   list = list.filter(s => new Date(s.createdAt) <= new Date(dateTo + 'T23:59:59'));
    list.sort((a, b) => sortDir === 'desc'
      ? new Date(b.createdAt) - new Date(a.createdAt)
      : new Date(a.createdAt) - new Date(b.createdAt)
    );
    return list;
  }, [students, search, dateFrom, dateTo, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hasExtra = search || dateFrom || dateTo || sortDir !== 'desc';
  const resetFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); setSortDir('desc'); setPage(1); };

  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await API.delete(`/admin/users/${delTarget._id}`);
      toast.success(`${delTarget.name} deleted`);
      setStudents(prev => prev.filter(s => s._id !== delTarget._id));
      setDelTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* ── HEADER ── */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle,rgba(43,0,87,0.4) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div>
            <div className="section-label">ADMIN PANEL</div>
            <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
              Manage <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Students</span>
            </h1>
            <p style={{ color: 'var(--muted)', marginTop: 8, fontSize: 14 }}>
              {filtered.length} student{filtered.length !== 1 ? 's' : ''} registered
            </p>
          </div>
        </div>
      </section>

      {/* ── FILTERS + TABLE ── */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40 }}>

          {/* Filter row */}
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }}><SearchIcon /></span>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by name, email or phone..."
                style={{ ...selectStyle, paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {/* Date From */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>From</span>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={selectStyle} />
            </div>

            {/* Date To */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>To</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={selectStyle} />
            </div>

            {/* Sort */}
            <select value={sortDir} onChange={e => { setSortDir(e.target.value); setPage(1); }} style={{ ...selectStyle, flex: '0 1 150px' }}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>

            {/* Clear */}
            {hasExtra && (
              <button onClick={resetFilters} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--danger)', background: 'transparent', color: 'var(--danger)', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 12.5, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(240,65,85,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <XIcon /> Clear
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--muted)', fontSize: 15 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
                Loading...
              </div>
            ) : paginated.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)', fontSize: 15 }}>
                No students match your filters.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                      {['#', 'Name', 'Email', 'Phone', 'Joined', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: 'var(--muted)', fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((student, i) => (
                      <tr key={student._id}
                        style={{ borderBottom: i < paginated.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13 }}>
                          {(page - 1) * PAGE_SIZE + i + 1}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{student.name}</div>
                        </td>
                        <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13 }}>{student.email}</td>
                        <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13 }}>{student.phone || '—'}</td>
                        <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {new Date(student.createdAt).toLocaleDateString('en-IN')}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button onClick={() => setSelected(student)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: `linear-gradient(135deg,${V1},${V3})`, color: '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                              <EyeIcon /> View
                            </button>
                            <button onClick={() => setDelTarget(student)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: 'transparent', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", border: '1.5px solid var(--danger)', cursor: 'pointer', transition: 'all 0.2s' }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(240,65,85,0.1)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <TrashIcon /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 28, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--card-solid)', color: 'var(--muted)', cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === 1 ? 0.4 : 1, transition: 'all 0.2s' }}>
                <ChevronLeftIcon />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} style={{ width: 36, height: 36, borderRadius: 8, cursor: 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 14, background: page === p ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)', color: page === p ? '#fff' : 'var(--muted)', border: `1.5px solid ${page === p ? V1 : 'var(--border)'}`, boxShadow: page === p ? `0 4px 16px ${V1}44` : 'none', transition: 'all 0.2s' }}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--card-solid)', color: 'var(--muted)', cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: page === totalPages ? 0.4 : 1, transition: 'all 0.2s' }}>
                <ChevronRightIcon />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── VIEW DETAIL MODAL ── */}
      {selected && (
        <div style={overlayStyle} onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: '36px 32px', maxWidth: 480, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', position: 'relative' }}>
            <button onClick={() => setSelected(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
              <XIcon />
            </button>
            {/* Avatar */}
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg,${V1},${V3})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: V2, marginBottom: 20 }}>
              <UserIcon />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Inter',sans-serif", marginBottom: 4 }}>{selected.name}</h2>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 24 }}>Student Account</p>
            {[
              { label: 'Email', value: selected.email },
              { label: 'Phone', value: selected.phone || '—' },
              { label: 'Verified', value: selected.isVerified ? '✅ Yes' : '❌ No' },
              { label: 'Joined', value: new Date(selected.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {delTarget && (
        <div style={overlayStyle} onClick={() => !deleting && setDelTarget(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: '36px 32px', maxWidth: 420, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(240,65,85,0.12)', border: '1px solid rgba(240,65,85,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'var(--danger)' }}>
              <TrashIcon />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Inter',sans-serif", marginBottom: 8 }}>Delete Student?</h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text)' }}>{delTarget.name}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setDelTarget(null)} disabled={deleting}
                style={{ padding: '10px 24px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'var(--danger)', color: '#fff', fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 14, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1, transition: 'opacity 0.2s' }}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminStudents;
